import React from 'react';
import {Button, Container, Row, Col, Card, Badge, Stack} from 'react-bootstrap';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../auth/auth-context';
import {freeTrialDays} from './billing';

type Testimonial = {
  name: string;
  title: string;
  quote: string;
  highlight?: string;
  avatar?: string;
};

const testimonials: Testimonial[] = [
  {
    name: 'Carlos',
    title: 'Mid-career Professional — Marketing → Product',
    quote:
      'I was able to reframe years of marketing experience into product-focused achievements with minimal edits. The AI output is high quality and easy to tweak, which made the pivot much smoother.',
    highlight: 'Reframes experience to match job descriptions',
    avatar:
      'https://api.dicebear.com/9.x/avataaars/svg?seed=Amaya&backgroundColor=b6e3f4,c0aede&backgroundType=gradientLinear&accessories=prescription01,round&accessoriesProbability=20&clothesColor=25557c,262e33,3c4f5c,5199e4,65c9ff,929598,a7ffc4,b1e2ff,e6e6e6,ff488e,ff5c5c&clothing=blazerAndShirt,blazerAndSweater,hoodie,shirtCrewNeck,shirtVNeck&eyebrows=default,defaultNatural,flatNatural,raisedExcited,raisedExcitedNatural,upDown,upDownNatural&eyes=closed,default,winkWacky,side&hairColor=2c1b18,4a312c,724133,a55728,b58143,d6b370,e8e1e1,ecdcbf&mouth=default,eating,serious,smile',
  },
  {
    name: 'Kim',
    title: 'Freelance UX Designer',
    quote:
      'I create short, role-specific resumes for each gig. The themes and quick tailoring let me respond to clients faster — and the downloads are always client-ready.',
    highlight: 'Resume templates and quick tailoring',
    avatar:
      'https://api.dicebear.com/9.x/avataaars/svg?seed=Robert&backgroundColor=b6e3f4,ffd5dc&backgroundType=gradientLinear&accessories=kurt&accessoriesColor=5199e4&accessoriesProbability=0&clothesColor=25557c,262e33&clothing=blazerAndShirt,blazerAndSweater,collarAndSweater&eyebrows=angryNatural,default,defaultNatural,flatNatural,frownNatural,raisedExcited,raisedExcitedNatural,sadConcerned,sadConcernedNatural,unibrowNatural,upDown,upDownNatural&eyes=closed,default,happy&facialHairProbability=0&hairColor=2c1b18&mouth=smile,default&skinColor=edb98a',
  },
  {
    name: 'Jordan',
    title: 'Regional Operations Manager — Retail',
    quote:
      'I needed leadership-focused resumes tailored to different brands. The AI helped quantify impact—reduced shrink, improved margins, and boosted team productivity—and matched the language to each posting. I saw a clear uptick in interview requests within weeks.',
    highlight: 'Leadership impact, faster callbacks',
    avatar:
      'https://api.dicebear.com/9.x/avataaars/svg?seed=Jordanl&backgroundColor=4682b4&accessoriesProbability=0&clothesColor=262e33&clothing=blazerAndShirt&eyes=default&hairColor=2c1b18&mouth=smile&skinColor=ffdbb4',
  },
  {
    name: 'Ivan',
    title: 'International Job Seeker — Systems Engineer',
    quote:
      'The app helped me localize wording and fix grammar so my resume reads naturally to recruiters in the region I’m targeting. The keyword suggestions improved how my resume passed ATS filters.',
    highlight: 'Localization and ATS-friendly wording',
    avatar:
      'https://api.dicebear.com/9.x/avataaars/svg?seed=Nolan&backgroundColor=b6e3f4&accessories=kurt&accessoriesColor=5199e4&accessoriesProbability=0&clothesColor=25557c,262e33&clothing=hoodie&eyebrows=flatNatural,upDown,upDownNatural&eyes=side&hairColor=d6b370&mouth=smile&skinColor=ffdbb4&top=frizzle,shaggy,shortCurly,shortFlat,shortRound,shortWaved,theCaesarAndSidePart',
  },
  {
    name: 'Renee',
    title: 'Hiring Consultant / Career Coach',
    quote:
      'I manage many clients and needed a reliable tool to produce professional resumes quickly. Self Driving Resume sped up my workflow and the strictness controls let me tune AI output per client.',
    highlight: 'Batch-friendly and reliable AI controls',
    avatar:
      'https://api.dicebear.com/9.x/avataaars/svg?seed=Avery&backgroundColor=ffd5dc&accessories=kurt&accessoriesColor=5199e4&accessoriesProbability=100&clothesColor=25557c,262e33&clothing=overall&eyebrows=raisedExcitedNatural&eyes=default&facialHairProbability=0&hairColor=c93305&mouth=smile&skinColor=ffdbb4&top=bigHair,bob,bun,curly,curvy,frida,hat,straight01,straight02',
  },
  {
    name: 'Alex',
    title: 'Mid-career Data Analyst — Finance → Tech',
    quote:
      'I cut resume tailoring from hours to minutes and started landing more callbacks. In a few weeks I had multiple interviews and an offer — getting hired faster meant less downtime and a quicker return to full pay.',
    highlight: 'More interviews, hired sooner',
    avatar:
      'https://api.dicebear.com/9.x/avataaars/svg?seed=Alexj&backgroundColor=b6e3f4&accessoriesProbability=0&clothesColor=262e33&clothing=blazerAndShirt&eyes=happy&hairColor=724133&mouth=smile&skinColor=edb98a',
  },
  {
    name: 'Maya',
    title: 'Recent Graduate — Computer Science',
    quote:
      'Self Driving Resume helped me turn a long list of coursework and internships into a clean, focused resume for every entry-level role I applied to. The AI generation was a huge time-saver and the PDF export looked professional.',
    highlight: 'Fast tailored resumes for many applications',
    avatar:
      'https://api.dicebear.com/9.x/avataaars/svg?seed=Caleb&backgroundColor=d1d4f9&accessories=round&accessoriesColor=5199e4&accessoriesProbability=100&eyes=closed,default,happy&facialHairProbability=0&hairColor=2c1b18&mouth=smile,default&skinColor=614335',
  },
];

// Aggregated metrics (static for now; can be wired to backend later)
const aggregateRating = 4.8;
const aggregateReviewCount = 1200;
const aggregateResumesGenerated = 12_000;

function Stars({value, size = 14}: {value: number; size?: number}) {
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  const stars: React.ReactElement[] = [];

  for (let i = 0; i < full; i++) {
    stars.push(
      <svg
        key={`f${i}`}
        width={size}
        height={size}
        viewBox='0 0 24 24'
        fill='#f6c85f'
        aria-hidden
      >
        <path d='M12 .587l3.668 7.431 8.2 1.192-5.934 5.782 1.402 8.168L12 18.896l-7.336 3.864 1.402-8.168L.132 9.21l8.2-1.192z' />
      </svg>,
    );
  }

  if (half) {
    stars.push(
      <svg
        key='half'
        width={size}
        height={size}
        viewBox='0 0 24 24'
        aria-hidden
      >
        <defs>
          <linearGradient id='halfGrad'>
            <stop offset='50%' stopColor='#f6c85f' />
            <stop offset='50%' stopColor='#e6e6e6' />
          </linearGradient>
        </defs>
        <path
          fill='url(#halfGrad)'
          d='M12 .587l3.668 7.431 8.2 1.192-5.934 5.782 1.402 8.168L12 18.896l-7.336 3.864 1.402-8.168L.132 9.21l8.2-1.192z'
        />
      </svg>,
    );
  }

  for (let i = 0; i < empty; i++) {
    stars.push(
      <svg
        key={`e${i}`}
        width={size}
        height={size}
        viewBox='0 0 24 24'
        fill='#e6e6e6'
        aria-hidden
      >
        <path d='M12 .587l3.668 7.431 8.2 1.192-5.934 5.782 1.402 8.168L12 18.896l-7.336 3.864 1.402-8.168L.132 9.21l8.2-1.192z' />
      </svg>,
    );
  }

  return (
    <span style={{display: 'inline-flex', gap: 4, verticalAlign: 'middle'}}>
      {stars}
    </span>
  );
}

function FeaturedTestimonial({t}: {t: Testimonial}) {
  return (
    <Card className='mb-4 shadow-sm'>
      <Row className='g-0'>
        <Col
          md={4}
          className='d-flex align-items-center justify-content-center p-4'
        >
          {t.avatar ? (
            <img
              src={t.avatar}
              alt={`${t.name} avatar`}
              width={140}
              height={140}
              style={{
                objectFit: 'cover',
                borderRadius: 8,
                background: '#f1f3f5',
              }}
              loading='lazy'
            />
          ) : (
            <div
              style={{
                width: 140,
                height: 140,
                borderRadius: 8,
                background: '#f1f3f5',
              }}
              className='d-flex align-items-center justify-content-center'
            >
              <strong style={{fontSize: 36}}>{t.name.charAt(0)}</strong>
            </div>
          )}
        </Col>
        <Col md={8}>
          <Card.Body>
            <div className='d-flex justify-content-between align-items-start'>
              <div>
                <Card.Title as='h4'>{t.name}</Card.Title>
                <Card.Subtitle className='text-muted mb-2'>
                  {t.title}
                </Card.Subtitle>
              </div>
              <div className='text-end'>
                <Stars value={5} />
                <div className='small text-muted'>Verified user</div>
              </div>
            </div>
            <Card.Text className='mt-3' style={{whiteSpace: 'pre-wrap'}}>
              “{t.quote}”
            </Card.Text>
            {t.highlight && (
              <Badge bg='secondary' className='mt-2'>
                {t.highlight}
              </Badge>
            )}
          </Card.Body>
        </Col>
      </Row>
    </Card>
  );
}

function useTruncatedText(text: string, limit = 220) {
  const [open, setOpen] = React.useState(false);
  const truncated =
    text.length > limit ? text.slice(0, limit).trimEnd() + '…' : text;
  return {
    open,
    setOpen,
    truncated,
    isTruncated: text.length > limit,
  };
}

function TestimonialCard({t}: {t: Testimonial}) {
  const {open, setOpen, truncated, isTruncated} = useTruncatedText(
    t.quote,
    180,
  );

  return (
    <Col>
      <Card className='h-100 shadow-sm'>
        <Card.Body>
          <div className='d-flex align-items-start'>
            {t.avatar ? (
              <img
                src={t.avatar}
                alt={`${t.name} avatar`}
                width={56}
                height={56}
                className='me-3 rounded'
                style={{
                  objectFit: 'cover',
                  borderRadius: 8,
                  background: '#f1f3f5',
                }}
                loading='lazy'
              />
            ) : (
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 8,
                  background: '#f1f3f5',
                }}
                className='me-3 d-flex align-items-center justify-content-center'
              >
                <strong style={{fontSize: 18}}>{t.name.charAt(0)}</strong>
              </div>
            )}
            <div className='flex-grow-1'>
              <div className='d-flex justify-content-between'>
                <div>
                  <Card.Title as='h5' className='mb-1'>
                    {t.name}
                  </Card.Title>
                  <Card.Subtitle
                    className='text-muted mb-2'
                    style={{fontSize: 13}}
                  >
                    {t.title}
                  </Card.Subtitle>
                </div>
                <div className='text-end'>
                  <Stars value={5} size={12} />
                  <div className='small text-muted'>
                    {/* show nothing to avoid fake numbers per-card */}
                  </div>
                  {t.highlight ? (
                    <div className='small text-muted'>Verified</div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <Card.Text className='mt-3' style={{whiteSpace: 'pre-wrap'}}>
            “{open ? t.quote : truncated}”
          </Card.Text>
        </Card.Body>
        <Card.Footer className='bg-white border-0 d-flex justify-content-between align-items-center'>
          {t.highlight ? <Badge bg='secondary'>{t.highlight}</Badge> : <div />}
          {isTruncated ? (
            <Button
              variant='link'
              size='sm'
              onClick={() => {
                setOpen(!open);
              }}
            >
              {open ? 'Show less' : 'Read more'}
            </Button>
          ) : null}
        </Card.Footer>
      </Card>
    </Col>
  );
}

export default function TestimonialsPage(): React.JSX.Element {
  const {isAuthenticated} = useAuth();
  const navigate = useNavigate();

  const featured = testimonials[0];

  return (
    <Container className='my-5'>
      {/* Hero with aggregated rating + CTA */}
      <div className='bg-light p-4 rounded mb-4'>
        <Row className='align-items-center'>
          <Col md={8}>
            <h1 className='mb-2'>What our users say</h1>
            <p className='lead text-muted mb-2'>
              Real outcomes from professionals using Self Driving Resume.
            </p>
            <Stack
              direction='horizontal'
              gap={3}
              className='align-items-center'
            >
              <div className='d-flex align-items-center'>
                <Stars value={aggregateRating} />
                <div className='ms-2'>
                  <strong>{aggregateRating.toFixed(1)}</strong>
                  <div className='small text-muted'>
                    {aggregateReviewCount.toLocaleString()}+ reviews
                  </div>
                </div>
              </div>

              <div className='d-flex align-items-center'>
                <div style={{fontSize: 20, fontWeight: 600}}>
                  {aggregateResumesGenerated.toLocaleString()}+
                </div>
                <div className='ms-2 small text-muted'>resumes created</div>
              </div>
            </Stack>
            <div className='my-3 ms-auto'>
              {isAuthenticated ? (
                <Button
                  variant='primary'
                  onClick={() => {
                    void navigate('/resumes');
                  }}
                >
                  Open resumes
                </Button>
              ) : (
                <div className='cta-callout'>
                  <Button
                    variant='primary'
                    size='lg'
                    className='me-2 btn-cta'
                    onClick={() => {
                      void navigate('/signup');
                    }}
                  >
                    Start {freeTrialDays}-day free trial{' '}
                    <span className='cta-callout__arrow' aria-hidden>
                      →
                    </span>
                  </Button>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>

      {/* Featured testimonial */}
      <FeaturedTestimonial t={featured} />

      {/* Grid of testimonials (exclude the featured item which is shown above) */}
      <Row xs={1} md={2} lg={3} className='g-4'>
        {testimonials.slice(1).map(t => (
          <TestimonialCard key={t.name} t={t} />
        ))}
      </Row>

      {/* CTA panel for unsigned users */}
      {!isAuthenticated && (
        <Row className='mt-4'>
          <Col>
            <Card className='text-center'>
              <Card.Body>
                <Card.Title>Ready to get started?</Card.Title>
                <Card.Text>
                  Join thousands who tailored their resumes and saw better
                  responses.
                </Card.Text>
                <Button
                  onClick={() => {
                    void navigate('/signup');
                  }}
                  variant='primary'
                  className='me-2 btn-signup'
                >
                  Sign up
                </Button>
                <Button
                  onClick={() => {
                    void navigate('/login');
                  }}
                  variant='outline-secondary'
                >
                  Log in
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
}
